import { useState } from "react";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import { toast } from "sonner";
import { FileText } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PilotResultsTab({ projectId, token, pilotResults, setPilotResults }) {
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/projects/${projectId}/improve/pilot-results`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(pilotResults),
      });
      if (!response.ok) throw new Error("Failed to save pilot results");
      toast.success("Pilot results saved");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6" data-testid="pilot-results-form">
      <div className="flex items-center gap-3 mb-1">
        <FileText className="w-5 h-5 text-primary" />
        <h3 className="font-heading font-semibold">Pilot Results</h3>
      </div>
      <p className="text-sm text-muted-foreground mb-6">
        Document the pilot outcomes. Required before gate advancement.
      </p>
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Before Metrics</Label>
            <Textarea value={pilotResults.before_metrics} onChange={(e) => setPilotResults(p => ({ ...p, before_metrics: e.target.value }))} placeholder="Baseline metrics before the pilot..." rows={3} data-testid="results-before" />
          </div>
          <div className="space-y-2">
            <Label>After Metrics</Label>
            <Textarea value={pilotResults.after_metrics} onChange={(e) => setPilotResults(p => ({ ...p, after_metrics: e.target.value }))} placeholder="Metrics observed after the pilot..." rows={3} data-testid="results-after" />
          </div>
        </div>
        <div className="space-y-2">
          <Label>Test Conclusion</Label>
          <Textarea value={pilotResults.test_conclusion} onChange={(e) => setPilotResults(p => ({ ...p, test_conclusion: e.target.value }))} placeholder="Was the hypothesis confirmed? What was the outcome?" rows={2} data-testid="results-conclusion" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>P-Value (optional)</Label>
            <Input value={pilotResults.p_value} onChange={(e) => setPilotResults(p => ({ ...p, p_value: e.target.value }))} placeholder="e.g., 0.003" data-testid="results-pvalue" />
          </div>
          <div className="space-y-2">
            <Label>Effect Size (optional)</Label>
            <Input value={pilotResults.effect_size} onChange={(e) => setPilotResults(p => ({ ...p, effect_size: e.target.value }))} placeholder="e.g., Cohen's d = 0.8" data-testid="results-effect-size" />
          </div>
        </div>
        <div className="space-y-2">
          <Label>Learnings</Label>
          <Textarea value={pilotResults.learnings} onChange={(e) => setPilotResults(p => ({ ...p, learnings: e.target.value }))} placeholder="Key takeaways, surprises, and recommendations..." rows={3} data-testid="results-learnings" />
        </div>
      </div>
      <Button onClick={save} disabled={saving} className="mt-4" data-testid="save-pilot-results-btn">
        {saving ? "Saving..." : "Save Pilot Results"}
      </Button>
    </div>
  );
}
